import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { errMsg } from "@/lib/api";
import { num } from "@/lib/sk";
import { Assumption, isFresh, isMissing, isReady, K, openLines, ProposalLines, ProposalSum } from "@/components/canvas/kalkyle/Forslag";

const btnPrimary = "inline-flex h-10 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] disabled:opacity-40";
const btnQuiet = "inline-flex h-10 items-center rounded-full px-3 text-[14px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

function ReadyPost({ p, checked, onCheck, onOpen, onUndo }) {
  return (
    <article className={cn("flex gap-3.5 border-t border-black/[0.06] py-5 first:border-t-0", p.applied && "opacity-60")} data-testid={`forslag-post-${p.ref}`} data-level={p.level} data-applied={p.applied ? "1" : "0"}>
      <div className="pt-[3px]">
        {p.applied ? <Check className="h-4 w-4 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> : <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} className="h-4 w-4 cursor-pointer accent-[hsl(var(--navy))]" aria-label={`Velg ${p.ref}`} data-testid={`forslag-check-${p.ref}`} />}
      </div>
      <div className="min-w-0 flex-1">
        <button type="button" onClick={() => onOpen(p.item_id)} className="flex w-full items-baseline gap-2.5 text-left" data-testid={`forslag-open-${p.ref}`}>
          <span className="shrink-0 font-mono text-[12.5px] text-muted-foreground">{p.ref}</span>
          <span className="min-w-0 flex-1 truncate text-[16px] font-semibold tracking-[-0.01em] text-foreground hover:underline">{p.title}</span>
        </button>
        <ProposalLines lines={p.lines} className="mt-3" />
        <ProposalSum p={p} testId={`forslag-sum-${p.ref}`} />
        <Assumption p={p} className="mt-2.5" />
        {p.applied ? <p className="mt-2.5 text-[13.5px] text-[hsl(var(--v-ok))]" data-testid={`forslag-applied-${p.ref}`}>Lagt inn{p.applied.change_id && onUndo ? <> · <button type="button" onClick={() => onUndo(p.applied.change_id)} className="text-muted-foreground hover:text-foreground hover:underline" data-testid={`forslag-undo-${p.ref}`}>Angre</button></> : null}</p> : null}
      </div>
    </article>
  );
}

function MissingPost({ p, onOpen }) {
  return (
    <li className="border-t border-black/[0.06] py-3.5 first:border-t-0" data-testid={`forslag-missing-${p.ref}`}>
      <button type="button" onClick={() => onOpen(p.item_id)} className="flex w-full items-baseline gap-2.5 text-left" data-testid={`forslag-open-${p.ref}`}>
        <span className="shrink-0 font-mono text-[12.5px] text-muted-foreground">{p.ref}</span>
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground hover:underline">{p.title}</span>
      </button>
      <p className="mt-1 pl-[46px] text-[14px] leading-[1.5] text-foreground/75">{p.question || p.why}</p>
      {p.question && p.why ? <p className="mt-0.5 pl-[46px] text-[13px] leading-[1.5] text-muted-foreground" data-testid={`forslag-missing-why-${p.ref}`}>{p.why}</p> : null}
      {p.lines?.length ? <p className="mt-0.5 pl-[46px] text-[13px] leading-[1.5] text-muted-foreground" data-testid={`forslag-missing-lines-${p.ref}`}>Oppsett når svaret er inne: {p.lines.map((l) => l.description).join(" · ")}</p> : null}
      {p.applied ? <p className="mt-1 pl-[46px] text-[13px] text-muted-foreground">Avklaring opprettet</p> : null}
    </li>
  );
}

/**
 * «Forslag til kalkyle» – the review is a mode of the work surface, not a sidebar. What is ready reads like the calculation
 * will; what is missing is one concrete question per post. Egil ticks, applies, or goes back without changing anything.
 */
export function Review({ forslag, ops, remaining, onBack, onOpen, onReviewOne }) {
  const { plan, state, analyse, apply, undo } = forslag;
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const started = useRef(false);
  const fresh = isFresh(plan);
  useEffect(() => { if (!fresh && !started.current && state !== "running") { started.current = true; analyse(null); } }, [fresh, state, analyse]);

  const items = useMemo(() => (fresh ? plan.items : []), [plan, fresh]);
  const ready = useMemo(() => items.filter((p) => isReady(p) || (p.applied && p.applied.mode === "legg_inn" && p.level !== "mangler")), [items]);
  const openReady = ready.filter(isReady);
  const missing = useMemo(() => items.filter((p) => isMissing(p) || (p.applied && p.applied.mode === "avklar")), [items]);
  const picked = openReady.filter((p) => !selected || selected.has(p.item_id));
  const askable = missing.filter((p) => isMissing(p) && !p.manual && p.question);
  const waiting = openReady.reduce((n, p) => n + openLines(p).length, 0);
  const toggle = (id, on) => setSelected((s) => { const nx = new Set(s || openReady.map((p) => p.item_id)); if (on) nx.add(id); else nx.delete(id); return nx; });

  const run = async (ids, mode) => {
    setBusy(true);
    try {
      const r = await apply(plan, ids, mode, ops);
      const done = ids.length - (r.skipped?.length || 0);
      toast.success(mode === "avklar" ? `${done} ${done === 1 ? "avklaring" : "avklaringer"} opprettet` : `${done} ${done === 1 ? "forslag lagt inn" : "forslag lagt inn"}`, r.change_id ? { action: { label: "Angre", onClick: () => undo(r.change_id, ops) } } : undefined);
      if (r.skipped?.length) toast(`${r.skipped.length} ${r.skipped.length === 1 ? "post" : "poster"} hoppet over – endret siden analysen`);
      setSelected(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const running = state === "running" || (!fresh && state !== "error");

  return (
    <div className="mx-auto w-full max-w-[820px] px-4 pb-20 pt-4 sm:px-8" data-testid="kalkyle-review" data-state={running ? "running" : "done"}>
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13.5px] font-medium text-foreground/60 transition-colors hover:bg-black/[0.05] hover:text-foreground" data-testid="review-back"><ChevronLeft className="h-4 w-4" /> Tilbake til kalkylen</button>
        {fresh && !running ? <button type="button" onClick={() => analyse(null)} className="text-[13.5px] text-muted-foreground hover:text-foreground" data-testid="review-rerun">Analyser på nytt</button> : null}
      </div>

      <header className="mt-6">
        <h2 className="text-[22px] font-semibold leading-[1.25] tracking-[-0.02em] sm:text-[26px]">Forslag til kalkyle</h2>
        {running ? (
          <p className="mt-2 text-[15.5px] text-muted-foreground" data-testid="review-running">Leser {remaining ? `${remaining} ${remaining === 1 ? "post" : "poster"}` : "postene"} mot prislisten …</p>
        ) : state === "error" && !fresh ? (
          <p className="mt-2 text-[15.5px] text-muted-foreground">Analysen stoppet. <button type="button" onClick={() => analyse(null)} className="font-medium text-[hsl(var(--navy))] hover:underline" data-testid="review-retry">Prøv igjen</button></p>
        ) : (
          <div className="mt-2 space-y-0.5 text-[15.5px] leading-[1.55]" data-testid="review-summary">
            <p className="text-foreground">{openReady.length ? <><strong className="font-semibold">{openReady.length} {openReady.length === 1 ? "forslag er klart" : "forslag er klare"}</strong></> : ready.length ? "Alle forslag er lagt inn" : "Ingen forslag kan legges inn ennå"}</p>
            {waiting ? <p className="text-muted-foreground" data-testid="review-awaiting">{waiting} {waiting === 1 ? "linje venter" : "linjer venter"} på pris eller mengde – legges inn åpne, så ingenting glemmes</p> : null}
            {missing.filter(isMissing).length ? <p className="text-muted-foreground">{missing.filter(isMissing).length} {missing.filter(isMissing).length === 1 ? "post mangler" : "poster mangler"} nødvendig grunnlag</p> : null}
            {plan?.totals?.alle?.price != null && openReady.length ? <p className="text-muted-foreground">Med alt lagt inn: {K(plan.totals.alle.price)} salg{plan.totals.alle.db_pct != null ? ` · ${num(plan.totals.alle.db_pct, 1)} % DB` : ""}{waiting ? " · foreløpig" : ""}</p> : null}
          </div>
        )}
      </header>

      {!running && ready.length ? (
        <section className="mt-10" data-testid="review-ready">
          <h3 className="text-[13.5px] font-semibold text-muted-foreground">Klare forslag</h3>
          <div className="mt-2">{ready.map((p) => <ReadyPost key={p.item_id} p={p} checked={!selected || selected.has(p.item_id)} onCheck={(on) => toggle(p.item_id, on)} onOpen={onOpen} onUndo={(cid) => undo(cid, ops)} />)}</div>
          {openReady.length ? (
            <div className="sticky bottom-[72px] mt-4 flex flex-wrap items-center gap-2 rounded-full bg-white/95 py-2 backdrop-blur-sm">
              <button type="button" disabled={!picked.length || busy} onClick={() => run(picked.map((p) => p.item_id), "legg_inn")} className={btnPrimary} data-testid="review-apply">Legg inn {picked.length} {picked.length === 1 ? "forslag" : "forslag"}</button>
              <button type="button" disabled={!openReady.length || busy} onClick={() => onReviewOne(openReady[0].item_id)} className={btnQuiet} data-testid="review-one-by-one">Gjennomgå ett og ett</button>
            </div>
          ) : null}
        </section>
      ) : null}

      {!running && missing.length ? (
        <section className="mt-10" data-testid="review-missing">
          <button type="button" onClick={() => setShowMissing((v) => !v)} aria-expanded={showMissing} className="flex w-full items-baseline gap-2.5 text-left" data-testid="review-missing-toggle">
            <h3 className="text-[13.5px] font-semibold text-muted-foreground">Mangler grunnlag</h3>
            <span className="text-[13px] text-muted-foreground">{missing.length} {missing.length === 1 ? "post" : "poster"}</span>
            <ChevronDown className={cn("ml-auto h-4 w-4 text-muted-foreground/60 transition-transform", !showMissing && "-rotate-90")} />
          </button>
          {showMissing ? (
            <>
              <ul className="mt-2">{missing.map((p) => <MissingPost key={p.item_id} p={p} onOpen={onOpen} />)}</ul>
              {askable.length ? <button type="button" disabled={busy} onClick={() => run(askable.map((p) => p.item_id), "avklar")} className={cn(btnQuiet, "mt-3 -ml-3")} data-testid="review-ask">Opprett {askable.length} {askable.length === 1 ? "avklaring" : "avklaringer"} på sakene</button> : null}
            </>
          ) : null}
        </section>
      ) : null}
      {!running && !items.length && fresh ? <p className="mt-10 text-[15px] text-muted-foreground" data-testid="review-empty">Alle poster er priset – ingenting å foreslå.</p> : null}
    </div>
  );
}
